import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, FlaskConical, Shield, FileCheck, Truck, Clock, Award, Beaker, X, ShoppingCart, FileText, Building2 } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { productAPI, seedAPI, coaAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useOptimizedImages } from '../context/OptimizedImageContext';
import { useRegional } from '../context/RegionalContext';
import COAModal from '../components/COAModal';
import SEOHead, { OrganizationSchema, WebSiteSchema } from '../components/SEO/SEOHead';
import { SEO_DATA } from '../components/SEO/seoData';

// Asset URLs
const API_BASE = process.env.REACT_APP_BACKEND_URL;
// Uniform Amidex container for all product fallbacks
const PRODUCT_IMAGES = {
  bpc157: "/amidex-container.png",
  ab701: "/amidex-container.png",
  glp1r: "/amidex-container.png",
  tb500: "/amidex-container.png",
};

// ============================================================
// FUTURISTIC BIOTECH HERO BACKGROUND
// Premium floating biological cells with glass-like aesthetics
// Apple + OpenAI + Biotech Research Lab aesthetic
// ============================================================

// CSS Keyframes for smooth floating animations
const floatAnimationStyles = `
  @keyframes float-slow {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    25% { transform: translateY(-12px) translateX(4px); }
    50% { transform: translateY(-6px) translateX(-3px); }
    75% { transform: translateY(-15px) translateX(2px); }
  }
  
  @keyframes float-medium {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    33% { transform: translateY(-18px) translateX(-5px); }
    66% { transform: translateY(-8px) translateX(6px); }
  }
  
  @keyframes float-fast {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    50% { transform: translateY(-10px) translateX(3px); }
  }
  
  @keyframes pulse-glow {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 0.9; }
  }
  
  @keyframes particle-drift {
    0%, 100% { transform: translate(0, 0); opacity: 0.4; }
    25% { transform: translate(2px, -3px); opacity: 0.7; }
    50% { transform: translate(-1px, -5px); opacity: 0.5; }
    75% { transform: translate(3px, -2px); opacity: 0.8; }
  }
`;

// SVG Definitions for reusable cell components
const CellDefinitions = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }}>
    <defs>
      {/* Glass-like membrane gradient */}
      <radialGradient id="membrane-gradient" cx="30%" cy="30%" r="70%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
        <stop offset="40%" stopColor="rgba(240,249,255,0.4)" />
        <stop offset="70%" stopColor="rgba(224,242,254,0.2)" />
        <stop offset="100%" stopColor="rgba(186,230,253,0.1)" />
      </radialGradient>
      
      {/* Nucleus glow gradient */}
      <radialGradient id="nucleus-gradient" cx="40%" cy="40%" r="60%">
        <stop offset="0%" stopColor="rgba(147,197,253,0.9)" />
        <stop offset="30%" stopColor="rgba(96,165,250,0.6)" />
        <stop offset="60%" stopColor="rgba(59,130,246,0.3)" />
        <stop offset="100%" stopColor="rgba(37,99,235,0.1)" />
      </radialGradient>
      
      {/* Inner glow for nucleus */}
      <radialGradient id="nucleus-inner-glow" cx="35%" cy="35%" r="50%">
        <stop offset="0%" stopColor="rgba(191,219,254,1)" />
        <stop offset="50%" stopColor="rgba(147,197,253,0.7)" />
        <stop offset="100%" stopColor="rgba(96,165,250,0.3)" />
      </radialGradient>
      
      {/* Membrane edge highlight */}
      <linearGradient id="membrane-edge" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.8)" />
        <stop offset="50%" stopColor="rgba(255,255,255,0.2)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </linearGradient>
      
      {/* Soft outer glow filter */}
      <filter id="cell-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="8" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      
      {/* Subtle blur for depth */}
      <filter id="depth-blur-light" x="-10%" y="-10%" width="120%" height="120%">
        <feGaussianBlur stdDeviation="1.5" />
      </filter>
      
      <filter id="depth-blur-medium" x="-10%" y="-10%" width="120%" height="120%">
        <feGaussianBlur stdDeviation="3" />
      </filter>
      
      {/* Reusable Biological Cell Symbol */}
      <symbol id="bio-cell" viewBox="0 0 200 200">
        {/* Outer glow aura */}
        <circle cx="100" cy="100" r="85" fill="rgba(147,197,253,0.08)" />
        
        {/* Main cell membrane - glass-like */}
        <circle 
          cx="100" 
          cy="100" 
          r="75" 
          fill="url(#membrane-gradient)"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="1"
        />
        
        {/* Inner membrane ring */}
        <circle 
          cx="100" 
          cy="100" 
          r="68" 
          fill="none"
          stroke="rgba(255,255,255,0.25)"
          strokeWidth="0.5"
        />
        
        {/* Nucleus with glow */}
        <circle 
          cx="90" 
          cy="90" 
          r="28" 
          fill="url(#nucleus-gradient)"
          filter="url(#cell-glow)"
        />
        
        {/* Nucleus inner core */}
        <circle 
          cx="88" 
          cy="88" 
          r="16" 
          fill="url(#nucleus-inner-glow)"
        />
        
        {/* Nucleolus - small bright spot */}
        <circle 
          cx="85" 
          cy="85" 
          r="5" 
          fill="rgba(191,219,254,0.95)"
        />
        
        {/* Light particles inside cell */}
        <g className="particles">
          <circle cx="130" cy="70" r="3" fill="rgba(147,197,253,0.5)" />
          <circle cx="145" cy="95" r="2" fill="rgba(191,219,254,0.4)" />
          <circle cx="125" cy="130" r="2.5" fill="rgba(147,197,253,0.45)" />
          <circle cx="70" cy="140" r="2" fill="rgba(191,219,254,0.35)" />
          <circle cx="55" cy="110" r="1.5" fill="rgba(147,197,253,0.4)" />
          <circle cx="150" cy="120" r="1.5" fill="rgba(191,219,254,0.3)" />
        </g>
        
        {/* Top highlight - glass reflection */}
        <ellipse 
          cx="75" 
          cy="55" 
          rx="25" 
          ry="10" 
          fill="url(#membrane-edge)"
          transform="rotate(-20, 75, 55)"
        />
        
        {/* Secondary highlight */}
        <ellipse 
          cx="65" 
          cy="70" 
          rx="8" 
          ry="4" 
          fill="rgba(255,255,255,0.6)"
          transform="rotate(-20, 65, 70)"
        />
        
        {/* Bottom rim light */}
        <ellipse 
          cx="115" 
          cy="155" 
          rx="20" 
          ry="4" 
          fill="rgba(255,255,255,0.15)"
        />
      </symbol>
    </defs>
  </svg>
);

// Individual Cell Instance Component
const BioCell = ({ 
  x, 
  y, 
  size, 
  opacity = 1, 
  animationDuration = 20,
  animationDelay = 0,
  blur = 'none',
  zIndex = 1 
}) => (
  <div
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: size,
      height: size,
      opacity,
      filter: blur,
      zIndex,
      animation: `float-slow ${animationDuration}s ease-in-out infinite`,
      animationDelay: `${animationDelay}s`,
      willChange: 'transform',
    }}
  >
    <svg viewBox="0 0 200 200" width="100%" height="100%">
      <use href="#bio-cell" />
    </svg>
  </div>
);

// ============================================================
// FUTURISTIC HERO BACKGROUND - Premium Biotech Aesthetic
// ============================================================
const FuturisticBioBackground = () => {
  const { scrollY } = useScroll();
  
  // Parallax transforms for depth layers
  const bgY = useTransform(scrollY, [0, 1000], [0, -20]);
  const midY = useTransform(scrollY, [0, 1000], [0, -50]);
  const fgY = useTransform(scrollY, [0, 1000], [0, -90]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Inject animation keyframes */}
      <style>{floatAnimationStyles}</style>
      
      {/* SVG Definitions */}
      <CellDefinitions />
      
      {/* Base background */}
      <div 
        className="absolute inset-0" 
        style={{ backgroundColor: '#FAFAFA' }} 
      />
      
      {/* Subtle gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 100% 100% at 50% 0%, rgba(240,249,255,0.5) 0%, transparent 50%),
            radial-gradient(ellipse 80% 50% at 80% 50%, rgba(224,242,254,0.3) 0%, transparent 40%),
            radial-gradient(ellipse 60% 40% at 20% 60%, rgba(186,230,253,0.2) 0%, transparent 35%)
          `
        }}
      />
      
      {/* === BACKGROUND LAYER - Smallest, most blurred === */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <BioCell x="5%" y="10%" size={60} opacity={0.25} animationDuration={35} animationDelay={0} blur="blur(4px)" zIndex={1} />
        <BioCell x="90%" y="25%" size={50} opacity={0.2} animationDuration={40} animationDelay={5} blur="blur(5px)" zIndex={1} />
        <BioCell x="15%" y="70%" size={45} opacity={0.18} animationDuration={38} animationDelay={8} blur="blur(5px)" zIndex={1} />
        <BioCell x="80%" y="75%" size={55} opacity={0.22} animationDuration={42} animationDelay={3} blur="blur(4px)" zIndex={1} />
      </motion.div>
      
      {/* === MID LAYER - Medium size, slight blur === */}
      <motion.div className="absolute inset-0" style={{ y: midY }}>
        <BioCell x="8%" y="5%" size={90} opacity={0.5} animationDuration={28} animationDelay={0} blur="blur(1.5px)" zIndex={2} />
        <BioCell x="75%" y="12%" size={80} opacity={0.45} animationDuration={32} animationDelay={4} blur="blur(2px)" zIndex={2} />
        <BioCell x="88%" y="50%" size={70} opacity={0.4} animationDuration={30} animationDelay={7} blur="blur(2px)" zIndex={2} />
        <BioCell x="3%" y="55%" size={75} opacity={0.42} animationDuration={34} animationDelay={2} blur="blur(1.5px)" zIndex={2} />
      </motion.div>
      
      {/* === FOREGROUND LAYER - Largest, sharpest === */}
      <motion.div className="absolute inset-0" style={{ y: fgY }}>
        <BioCell x="4%" y="15%" size={140} opacity={0.75} animationDuration={22} animationDelay={0} blur="none" zIndex={3} />
        <BioCell x="72%" y="3%" size={130} opacity={0.7} animationDuration={25} animationDelay={3} blur="none" zIndex={3} />
        <BioCell x="85%" y="40%" size={100} opacity={0.6} animationDuration={26} animationDelay={6} blur="blur(0.5px)" zIndex={3} />
      </motion.div>
    </div>
  );
};

// Modal fallback image
const MODAL_FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f8fafc' width='400' height='400'/%3E%3Ctext x='50%25' y='45%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='48' font-weight='600' fill='%23e2e8f0'%3EAMIDEX%3C/text%3E%3Ctext x='50%25' y='58%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='14' fill='%23cbd5e1'%3EBIOTECH%3C/text%3E%3C/svg%3E";

// Product Modal Component
const ProductModal = ({ product, isOpen, onClose, onAddToCart, onViewCOA, regionalPrice }) => {  const { region } = useRegional();
  
  if (!isOpen || !product) return null;

  const handleViewCOA = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onViewCOA) {
      onViewCOA(product);
    }
  };

  // Get product image with fallback
  const getModalImage = () => {
    if (product.image_url) return product.image_url;
    if (product.images && product.images.length > 0) return product.images[0];
    if (product.media && product.media.length > 0) return product.media[0];
    return MODAL_FALLBACK_IMAGE;
  };
  
  // Get display price - use regional price if available
  const getDisplayPrice = () => {
    if (regionalPrice) {
      return regionalPrice.price_formatted || `${region.symbol}${regionalPrice.price?.toFixed(2)}`;
    }
    return `${region.symbol}${product.price?.toFixed(2) || '0.00'}`;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="modal-backdrop"
      />

      <div
        className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-modal-in"
        data-testid="product-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
          data-testid="modal-close-btn"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          <div className="bg-gradient-to-br from-white via-slate-50 to-white p-8 flex items-center justify-center min-h-[300px] border-r border-slate-100">
            <img
              src={getModalImage()}
              alt={product.name}
              onError={(e) => { e.target.src = MODAL_FALLBACK_IMAGE; }}
              className="max-w-full max-h-64 object-contain"
            />
          </div>

          <div className="p-8">
            <span className="inline-block text-xs font-semibold tracking-wider text-cyan-600 uppercase mb-2">
              {'Analytical Reagent'}
            </span>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
              {product.name}
            </h2>
            <p className="text-2xl font-bold text-cyan-600 mb-4">
              {getDisplayPrice()}
            </p>

            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              {product.description || 'Analytical-grade laboratory reagent for institutional use.'}
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm">
                <FlaskConical className="w-4 h-4 text-cyan-600" />
                <span className="text-slate-500">{'Assay Standard'}:</span>
                <span className="font-semibold text-green-600">{product.purity || "Analytical Grade"}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Shield className="w-4 h-4 text-cyan-600" />
                <span className="text-slate-500">{'Batch'}:</span>
                <span className="font-mono text-slate-700">{product.batch_info || "AB-2024-001"}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <FileCheck className="w-4 h-4 text-blue-600" />
                <span className="text-slate-500">TDS:</span>
                <span className="text-blue-600 font-medium">{'Available'}</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  onAddToCart(product._id || product.id, 1);
                  onClose();
                }}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-slate-800 transition-colors"
                data-testid="modal-add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                {'Add to Cart'}
              </button>
              <button
                onClick={handleViewCOA}
                className="w-full flex items-center justify-center gap-2 border border-slate-200 text-slate-700 px-6 py-3 rounded-full font-semibold hover:bg-slate-50 transition-colors"
                data-testid="modal-view-coa-btn"
              >
                <FileText className="w-5 h-5" />
                {'View TDS Report'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Branded fallback image - light background with Amidex logo
const CAROUSEL_FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f8fafc' width='400' height='400'/%3E%3Ctext x='50%25' y='45%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='48' font-weight='600' fill='%23e2e8f0'%3EAMIDEX%3C/text%3E%3Ctext x='50%25' y='58%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='14' fill='%23cbd5e1'%3EBIOTECH%3C/text%3E%3C/svg%3E";

// Lab Image - use image proxy for optimized delivery
const LAB_IMAGE_ORIGINAL = "https://images.pexels.com/photos/9574558/pexels-photo-9574558.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
// Optimized proxy URL - 400px is sufficient for displayed 362px (saves 35KB)
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const LAB_IMAGE_PROXY = `${API_BASE_URL}/api/images/proxy?url=${encodeURIComponent(LAB_IMAGE_ORIGINAL)}&w=400&q=80&fmt=webp`;

// Lab Image Component - Uses optimized proxy image
const LabImage = () => {
  const [imgSrc, setImgSrc] = useState(LAB_IMAGE_PROXY);
  
  return (
    <div className="relative aspect-square rounded-2xl overflow-hidden shadow-2xl border border-slate-100">
      <img 
        src={imgSrc}
        alt="Amidex Laboratory - Amino Acid Derivative Research and Quality Control"
        loading="eager"
        decoding="async"
        fetchPriority="high"
        width="500"
        height="500"
        className="w-full h-full object-cover"
        onError={() => setImgSrc(LAB_IMAGE_ORIGINAL)} // Fallback to original if proxy fails
      />
    </div>
  );
};

// Product Carousel Component
const ProductCarousel = ({ products, onProductClick, fallbackProducts }) => {
  const { getOptimizedUrl, getOptimizedData, loading: imagesLoading } = useOptimizedImages();
  const { region } = useRegional();
  const [regionalPrices, setRegionalPrices] = useState({});
  
  const autoplayPlugin = useRef(
    Autoplay({ 
      delay: 4000, 
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', containScroll: 'trimSnaps' },
    [autoplayPlugin.current]
  );

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
    autoplayPlugin.current.reset();
  }, [emblaApi]);
  
  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
    autoplayPlugin.current.reset();
  }, [emblaApi]);

  // Memoize displayProducts to prevent infinite re-renders
  const displayProducts = React.useMemo(() => {
    return products.length > 0 ? products.slice(0, 4) : fallbackProducts.slice(0, 4);
  }, [products, fallbackProducts]);
  
  const extendedProducts = [...displayProducts, ...displayProducts];

  // Fetch regional prices - only for real products from the database
  useEffect(() => {
    const fetchRegionalPrices = async () => {
      if (displayProducts.length === 0) return;
      
      const prices = {};
      await Promise.all(
        displayProducts.map(async (product) => {
          const productId = product._id || product.id;
          // Skip placeholder IDs (numeric strings like '1', '2', '3', '4')
          if (!productId || /^\d+$/.test(productId)) return;
          
          try {
            const response = await fetch(
              `${API_BASE}/api/regional/price/${productId}?region_code=${region.code}&_t=${Date.now()}`
            );
            if (response.ok) {
              const data = await response.json();
              prices[productId] = data.regional_price;
            }
          } catch (error) {
            // Silent fail - use default price
          }
        })
      );
      setRegionalPrices(prices);
    };
    
    fetchRegionalPrices();
  }, [displayProducts, region.code]);

  const getDisplayPrice = (product) => {
    const productId = product._id || product.id;
    const regionalPrice = regionalPrices[productId];
    if (regionalPrice) {
      return regionalPrice.price_formatted || `${region.symbol}${regionalPrice.price?.toFixed(2)}`;
    }
    return `${region.symbol}${product.price?.toFixed(2) || '49.99'}`;
  };

  // Helper to check if product is a blend
  const isBlend = (product) => {
    const name = (product.name || '').toLowerCase();
    const category = (product.category || '').toLowerCase();
    return name.includes('blend') || category.includes('blend');
  };

  // Get display name without "Blend" suffix
  const getDisplayName = (product) => {
    return product.name.replace(/\s*Blend\s*/gi, '').trim();
  };

  // Get product image with fallback - use optimized version if available
  const getProductImage = (product) => {
    const originalUrl = product.image_url || 
      (product.images && product.images.length > 0 ? product.images[0] : null) ||
      (product.media && product.media.length > 0 ? product.media[0] : null);
    
    if (!originalUrl) return CAROUSEL_FALLBACK_IMAGE;
    
    // Try to get optimized WebP thumbnail (400px for carousel)
    const optimizedUrl = getOptimizedUrl(originalUrl, { format: 'webp', thumbnail: 400 });
    return optimizedUrl || originalUrl;
  };

  // Get optimized image data for <picture> element
  const getImageData = (product) => {
    const originalUrl = product.image_url || 
      (product.images && product.images.length > 0 ? product.images[0] : null) ||
      (product.media && product.media.length > 0 ? product.media[0] : null);
    
    if (!originalUrl) return null;
    return getOptimizedData(originalUrl);
  };

  // Render optimized image with <picture> element
  const renderProductImage = (product) => {
    const imageData = getImageData(product);
    const fallbackSrc = getProductImage(product);
    
    // If we have optimized data, use picture element with srcset
    // WebP first (better iOS Safari support), then JPEG fallback
    if (imageData && imageData.thumbnails) {
      const webpSrcset = imageData.thumbnails.webp?.map(t => `${t.url} ${t.size}w`).join(', ');
      const jpegSrcset = imageData.thumbnails.jpeg?.map(t => `${t.url} ${t.size}w`).join(', ');
      // Carousel displays at 280px, on mobile ~140px (50vw on 280px viewport max)
      const sizes = "(max-width: 640px) 140px, 280px";
      
      return (
        <picture>
          {webpSrcset && <source type="image/webp" srcSet={webpSrcset} sizes={sizes} />}
          <img 
            src={imageData.thumbnails.jpeg?.[0]?.url || fallbackSrc}
            srcSet={jpegSrcset}
            sizes={sizes}
            alt={product.name}
            loading="lazy"
            decoding="async"
            width="280"
            height="280"
            onError={(e) => { e.target.src = CAROUSEL_FALLBACK_IMAGE; }}
            className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-110"
            style={{ backgroundColor: imageData.dominantColor }}
          />
        </picture>
      );
    }
    
    // Fallback to regular img
    return (
      <img 
        src={fallbackSrc}
        alt={product.name}
        loading="lazy"
        decoding="async"
        width="280"
        height="280"
        onError={(e) => { e.target.src = CAROUSEL_FALLBACK_IMAGE; }}
        className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-110"
      />
    );
  };

  return (
    <div className="relative w-full" data-testid="product-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {extendedProducts.map((product, i) => (
            <div 
              key={`${product._id || i}-${i}`}
              className="flex-none w-[280px] px-3 cursor-pointer"
              onClick={() => onProductClick(product)}
              data-testid={`carousel-product-${i % displayProducts.length}`}
            >
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                {/* Image Container - Light background with balanced padding */}
                <div className="relative h-[220px] bg-gradient-to-br from-white via-slate-50/50 to-white">
                  {/* Blend Badge - Positioned with consistent spacing */}
                  {isBlend(product) && (
                    <div className="absolute top-3 right-3 z-10">
                      <span className="bg-cyan-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-md">
                        {'Blend'}
                      </span>
                    </div>
                  )}
                  {/* Centered image with equal padding */}
                  <div className="absolute inset-4 flex items-center justify-center">
                    {renderProductImage(product)}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                    <span className="text-white text-xs font-semibold bg-cyan-600 px-4 py-2 rounded-full shadow-lg">
                      {'View Product'}
                    </span>
                  </div>
                </div>
                <div className="p-5 border-t border-slate-100">
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-1 truncate">{getDisplayName(product)}</h3>
                  <p className="text-sm text-slate-500 mb-3 truncate">{product.dosage}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-cyan-600">
                      {getDisplayPrice(product)}
                    </span>
                    <span className="text-sm font-medium text-slate-500 group-hover:text-cyan-600 transition-colors flex items-center gap-1">
                      {'View'} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={scrollPrev}
        aria-label="Previous products"
        className="absolute -left-5 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:scale-105 transition-all z-10"
        data-testid="carousel-prev"
      >
        <ChevronLeft className="w-5 h-5 text-slate-700" />
      </button>
      <button
        onClick={scrollNext}
        aria-label="Next products"
        className="absolute -right-5 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:scale-105 transition-all z-10"
        data-testid="carousel-next"
      >
        <ChevronRight className="w-5 h-5 text-slate-700" />
      </button>
    </div>
  );
};

// Main HomePage - NORMAL CONTINUOUS SCROLL
const HomePage = () => {  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductPrice, setSelectedProductPrice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [coaModalOpen, setCOAModalOpen] = useState(false);
  const [selectedCOA, setSelectedCOA] = useState(null);
  const [selectedCOAProduct, setSelectedCOAProduct] = useState(null);
  const [productCOAs, setProductCOAs] = useState({});
  const { addToCart } = useCart();
  const { region } = useRegional();

  useEffect(() => {
    const init = async () => {
      try { await seedAPI.seed(); } catch (e) { /* already seeded */ }
      try {
        const response = await productAPI.getAll();
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };
    init();
  }, []);

  // Fetch regional price when product is selected
  useEffect(() => {
    const fetchSelectedPrice = async () => {
      if (!selectedProduct) {
        setSelectedProductPrice(null);
        return;
      }
      
      const productId = selectedProduct._id || selectedProduct.id;
      try {
        const response = await fetch(
          `${API_BASE}/api/regional/price/${productId}?region_code=${region.code}&_t=${Date.now()}`
        );
        if (response.ok) {
          const data = await response.json();
          setSelectedProductPrice(data.regional_price);
        }
      } catch (error) {
        setSelectedProductPrice(null);
      }
    };
    
    fetchSelectedPrice();
  }, [selectedProduct, region.code]);

  // Handler to fetch and display TDS for a product
  const handleViewCOA = async (product) => {
    const productId = product._id || product.id;
    setSelectedCOAProduct(product); // Store the product for the modal
    
    // Check if we already have this product's TDS cached
    if (productCOAs[productId]) {
      setSelectedCOA(productCOAs[productId]);
      setCOAModalOpen(true);
      return;
    }
    
    try {
      const response = await coaAPI.getByProduct(productId);
      const coas = response.data;
      
      if (coas && coas.length > 0) {
        // Get the most recent active TDS
        const activeCOA = coas.find(c => c.is_active) || coas[0];
        const coaData = {
          ...activeCOA,
          product_name: product.name,
          product_category: product.category,
        };
        
        // Cache it
        setProductCOAs(prev => ({ ...prev, [productId]: coaData }));
        setSelectedCOA(coaData);
        setCOAModalOpen(true);
      } else {
        // No TDS found - show placeholder
        const placeholderCOA = {
          product_name: product.name,
          product_category: product.category,
          batch_number: product.batch_info || 'AB-2024-001',
          purity: product.purity || '99',
          lab_name: 'Analytical Facility',
          test_date: new Date().toISOString(),
          coa_url: null,
          notes: 'TDS documentation available upon request. Contact support@amidex.org for specific batch certificates.'
        };
        setSelectedCOA(placeholderCOA);
        setCOAModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch TDS:', error);
      // Show placeholder on error
      const placeholderCOA = {
        product_name: product.name,
        product_category: product.category,
        batch_number: product.batch_info || 'AB-2024-001',
        purity: product.purity || '99',
        lab_name: 'Analytical Facility',
        test_date: new Date().toISOString(),
        coa_url: null,
        notes: 'TDS documentation available upon request. Contact support@amidex.org for specific batch certificates.'
      };
      setSelectedCOA(placeholderCOA);
      setCOAModalOpen(true);
    }
  };

  // Combine schemas for homepage
  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [OrganizationSchema, WebSiteSchema],
  };

  return (
    <div ref={containerRef} className="relative -mt-[104px]" style={{ backgroundColor: '#FAFAFA' }} data-testid="home-page">
      {/* SEO Head */}
      <SEOHead
        title={SEO_DATA.home.title}
        description={SEO_DATA.home.description}
        canonical="/"
        keywords={SEO_DATA.home.keywords}
        structuredData={combinedSchema}
      />
      
      {/* CSS for modal animation */}
      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in { animation: modal-in 0.3s ease-out; }
      `}</style>

      {/* ===== FUTURISTIC BIOTECH BACKGROUND - Premium floating cells ===== */}
      <div className="absolute top-0 left-0 right-0 h-[200vh] overflow-hidden pointer-events-none z-0">
        <FuturisticBioBackground />
      </div>

      {/* ===== HERO SECTION ===== */}
      <section className="relative h-auto md:h-[70vh] lg:h-[75vh] flex items-center pt-[140px] pb-8 md:pt-[160px] md:pb-12">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-32 text-center relative z-10">
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl text-slate-900 leading-[1.05] mb-6 tracking-tight font-heading font-semibold"
          >
            {'Precision Laboratory Equipment'}
            <span className="block">
              <span className="text-blue-600">{'& Analytical Standards'}</span>
            </span>
          </h1>
          
          <p 
            className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-6 leading-relaxed font-body"
          >
            {'Supplying university research laboratories and biotech facilities with certified scientific hardware, consumables, and biochemical reagents.'}
          </p>

          {/* B2B Notice Banner */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 font-medium">
              <Building2 className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              {'FOR INSTITUTIONAL LABORATORY USE ONLY — Not for consumer, diagnostic, or therapeutic application'}
            </div>
          </div>

          {/* Feature Badges */}
          <div 
            className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 mb-8 text-sm text-slate-500 font-medium tracking-wide"
          >
            <span>{'B2B Institutional Only'}</span>
            <span className="hidden sm:inline text-slate-300">•</span>
            <span>{'Certified Reagent Grade'}</span>
            <span className="hidden sm:inline text-slate-300">•</span>
            <span>{'Tracked Global Logistics'}</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/products')}
              className="px-8 py-3.5 bg-slate-900 text-white rounded-full hover:bg-slate-800 hover:shadow-xl transition-all flex items-center gap-2 font-semibold"
              data-testid="hero-cta-btn"
            >
              {'Browse Catalog'}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/research')}
              className="px-8 py-3.5 border-2 border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 hover:border-slate-400 transition-all font-medium"
            >
              {'Technical Reference'}
            </button>
          </div>
        </div>
      </section>

      {/* ===== TRUSTED BY INSTITUTIONS SECTION ===== */}
      <section className="relative py-16 overflow-hidden" style={{ backgroundColor: '#FAFAFA', contentVisibility: 'auto', containIntrinsicSize: '0 300px' }}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-slate-500 text-xs font-semibold tracking-[0.25em] uppercase">
              Trusted By
            </span>
            <h2 className="font-heading font-medium text-slate-700 mt-3 text-lg md:text-xl">
              Institutional accounts across the United States and Europe
            </h2>
          </div>

          {/* Marquee container */}
          <div
            className="relative overflow-hidden"
            style={{
              maskImage: 'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
            }}
          >
            <div className="flex gap-12 md:gap-16 animate-trusted-marquee whitespace-nowrap">
              {/* Duplicate the list twice for seamless loop */}
              {[...Array(2)].map((_, iteration) => (
                <div key={iteration} className="flex gap-12 md:gap-16 flex-shrink-0">
                  {[
                    'Ivy League Research Labs',
                    'NIH-Funded Studies',
                    'R1 Research Universities',
                    'Federal Research Agencies',
                    'Top-25 Institutional Schools',
                    'Cancer Research Centers',
                    'Contract Research Organizations',
                    'Biotech Startups',
                    'Institutional Research Accounts',
                    'Academic Chemistry Departments',
                  ].map((label, idx) => (
                    <div
                      key={`${iteration}-${idx}`}
                      className="flex items-center gap-3 text-slate-400 hover:text-slate-600 transition-colors duration-300"
                    >
                      {/* Small institutional crest icon in uniform grey */}
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="flex-shrink-0"
                      >
                        <path d="M12 2 L4 6 v6 c0 5 3.5 8.5 8 10 4.5 -1.5 8 -5 8 -10 V6 z" />
                        <path d="M9 12 h6 M12 9 v6" />
                      </svg>
                      <span className="text-sm md:text-base font-medium tracking-wide">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Verification note */}
          <div className="text-center mt-10">
            <p className="text-xs text-slate-400 max-w-2xl mx-auto">
              Institutional categories representing the research community we serve. All accounts undergo manual verification against institutional affiliation before order fulfillment.
            </p>
          </div>
        </div>

        {/* Marquee animation keyframes */}
        <style>{`
          @keyframes trusted-marquee {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-trusted-marquee {
            animation: trusted-marquee 45s linear infinite;
            width: max-content;
          }
          .animate-trusted-marquee:hover {
            animation-play-state: paused;
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-trusted-marquee {
              animation-duration: 120s;
            }
          }
        `}</style>
      </section>

      {/* ===== SCIENCE & QUALITY SECTION (Two-column layout) ===== */}
      <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#FAFAFA', contentVisibility: 'auto', containIntrinsicSize: '0 800px' }}>
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            
            {/* Left side - Text content */}
            <div className="order-2 lg:order-1">
              <span className="text-blue-600 text-sm font-medium tracking-widest uppercase mb-4 block">
                {'Quality Assurance'}
              </span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 leading-tight mb-6">
                {'Scientifically Tested.'}<br />
                <span className="text-blue-600">{'Ethically Produced.'}</span>
              </h2>
              
              <div className="space-y-4 text-slate-600 leading-relaxed mb-8">
                <p>
                  {'Every batch of our amino acid derivatives is manufactured in a controlled laboratory environment that follows strict scientific and safety protocols.'}
                </p>
                <p>
                  {'We work with certified facilities that adhere to cGMP standards, ensuring consistency, assay standard, and traceability from production to final delivery.'}
                </p>
                <p>
                  {'Our commitment is to transparency — every product is accompanied by lot-specific Technical Data Sheets so institutional laboratory personnel can verify assay characterization before use.'}
                </p>
              </div>
            </div>
            
            {/* Right side - Lab image */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative max-w-md mx-auto">
                {/* Background glow */}
                <div className="absolute -inset-4 bg-gradient-to-br from-blue-100/50 via-cyan-50/30 to-transparent rounded-3xl blur-xl" />
                
                {/* Image container - square format */}
                <LabImage />
                
                {/* Decorative elements */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-100 rounded-full opacity-50 blur-2xl" />
                <div className="absolute -top-4 -left-4 w-20 h-20 bg-cyan-100 rounded-full opacity-40 blur-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST / QUALITY SECTION ===== */}
      <section id="quality-section" className="relative py-24 backdrop-blur-sm" style={{ backgroundColor: 'rgba(250, 250, 250, 0.8)', contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <span className="text-blue-600 text-sm font-medium tracking-widest uppercase mb-4 block">
              {'Laboratory Excellence'}
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 mb-6">
              {'Synthesized for precision'}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              {'Every compound undergoes rigorous synthesis protocols and multi-stage verification. Our commitment to assay standard is documented in every Technical Data Sheet.'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: 'Quality Assurance', desc: 'All hardware, consumables, and analytical standards conform to strict industrial manufacturing specifications.', stat: 'Standardized' },
              { icon: FileCheck, title: 'Technical Data Documentation', desc: 'Technical Data Sheets (TDS) and safety documentation provided with all institutional orders.', stat: 'Every Order' },
              { icon: FlaskConical, title: 'Direct B2B Logistics', desc: 'Tracked, temperature-controlled commercial dispatch direct to verified institutional facilities.', stat: 'Tracked' },
            ].map((item, i) => (
              <div 
                key={i} 
                className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-blue-600 mb-2">{item.stat}</div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SHIPPING / HANDLING SECTION ===== */}
      <section className="relative py-24" style={{ backgroundColor: '#FAFAFA', contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-600 text-sm font-medium tracking-widest uppercase mb-4 block">
              {'Institutional Fulfillment'}
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 mb-6">
              {'Standard Fulfillment'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {'Standard fulfillment within 3-5 business days to verified institutional addresses. All shipments include full product documentation, lot-specific Technical Data Sheet, and institutional packing slip.'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { 
                icon: Building2, 
                title: 'Institutions Only', 
                desc: 'Verified .edu/.gov/.org' 
              },
              { icon: FileText, title: 'Full Documentation', desc: 'TDS & packing slip included' },
              { icon: Beaker, title: 'Analytical Grade', desc: 'High assay standard' },
              { icon: Clock, title: '3-5 Business Days', desc: 'Standard fulfillment' },
            ].map((item, i) => (
              <div 
                key={i} 
                className="bg-white rounded-2xl p-6 text-center border border-slate-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA SECTION ===== */}
      <section className="relative py-24 bg-slate-900 text-white z-10" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl mb-6">
            {'Apply for Institutional Access'}
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-4">
            {'Register with your institutional email (.edu, .org, .gov) to access our analytical-grade amino acid derivative catalog. Manual verification within 48 hours.'}
          </p>
          <p className="text-sm text-slate-400 max-w-xl mx-auto mb-10">
            {'FOR INSTITUTIONAL LABORATORY USE ONLY — All products are supplied strictly for non-clinical laboratory analysis and industrial processing. Not for consumer, diagnostic, or therapeutic use.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-white text-slate-900 rounded-full font-semibold text-lg hover:bg-slate-100 transition-colors flex items-center gap-2"
            >
              {'Apply for Access'}
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/institutional-access')}
              className="px-8 py-4 border border-slate-600 text-white rounded-full font-semibold hover:bg-slate-800 transition-colors"
            >
              {'Learn More'}
            </button>
          </div>
        </div>
      </section>

      {/* Product Modal */}
      {isModalOpen && (
        <ProductModal
          product={selectedProduct}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProduct(null);
            setSelectedProductPrice(null);
          }}
          onAddToCart={addToCart}
          onViewCOA={handleViewCOA}
          regionalPrice={selectedProductPrice}
        />
      )}

      {/* TDS Modal */}
      <COAModal 
        isOpen={coaModalOpen} 
        onClose={() => {
          setCOAModalOpen(false);
          setSelectedCOA(null);
          setSelectedCOAProduct(null);
        }} 
        coa={selectedCOA}
        product={selectedCOAProduct}
      />
    </div>
  );
};

export default HomePage;
